type MaybeVoid<R> = R | void;

interface ReturnFunc<A extends any[], R> {
    (...args: A): Promise<MaybeVoid<R>>;
    /**
     * 节流函数中调用此方法会取消上一次的节流时间限制
     *
     * 防抖函数中，若开启了 ‘立即执行’，调用此方法会取消单次的时间限制
     *
     * 防抖函数中，若未开启开启 ‘立即执行’，调用此方法会取消最后一次的函数执行
     */
    cancel: () => void;
}

/**
 * 节流
 * @param func 节流函数
 * @param delay 间隔时间
 * @returns Promise<原函数的返回结果 || undefined>
 */
export function throttle<A extends any[], R>(
    func: (...args: A) => R,
    delay = 1000,
): ReturnFunc<A, R> {
    let timer: number;
    let result: Promise<R>;
    const ret = function (this: any, ...args: A) {
        if (!timer) {
            timer = 1;
            setTimeout(() => (timer = 0), delay);
            result = new Promise((resolve) => resolve(func.apply(this, args)));
        }
        return result;
    };
    ret.cancel = () => {
        clearTimeout(timer);
        timer = 0;
    };
    return ret;
}

/**
 * 防抖
 * @param func 防抖函数
 * @param delay 间隔时间，单位：ms，默认：500
 * @param immediate 是否立即执行，默认：true
 * @returns Promise<原函数的返回结果 || undefined>
 */
export function debounce<A extends any[], R>(
    func: (...args: A) => R,
    delay = 500,
    immediate = true,
): ReturnFunc<A, R> {
    let timer: number | NodeJS.Timeout;
    let result: Promise<MaybeVoid<R>>;
    let resolve: (value: MaybeVoid<R>) => void;
    const ret = function (this: any, ...args: A) {
        resolve && resolve();
        clearTimeout(timer);
        if (immediate) {
            result = new Promise<MaybeVoid<R>>((res) => {
                resolve = res;
                if (!timer) resolve(func.apply(this, args));
                timer = setTimeout(() => (timer = 0), delay);
            });
        } else {
            result = new Promise<MaybeVoid<R>>((res) => {
                resolve = res;
                timer = setTimeout(() => {
                    resolve(func.apply(this, args));
                }, delay);
            });
        }
        return result;
    };
    ret.cancel = () => {
        clearTimeout(timer);
        timer = 0;
    };
    return ret;
}

export const getRandom = (min = 5, max = 15) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
};
